import { PlusOutlined } from "@ant-design/icons"
import { COLOR } from "@finway-group/shared/lib/consts"
import { Expense } from "@finway-group/shared/lib/models/expense/expense.model"
import { LineItem } from "@finway-group/shared/lib/models/expense/expenseSplit.model"
import { formatCurrencyNumber } from "@finway-group/shared/lib/utils"
import { calculateTotalAmountInvoiced, calculateTotalQuantityReceived } from "@finway-group/shared/lib/utils/purchaseOrder.util"
import * as Sentry from "@sentry/react"
import { Button, Modal, Spin, Tag, Tooltip } from "antd"
import { LinkIcon } from "Components/icons/LinkIcon"
import { PurchaseOrderTagIcon } from "Components/icons/PurchaseOrderTagIcon"
import { UnlinkIcon } from "Components/icons/UnlinkIcon"
import { Loading } from "Components/loading"
import { AIIcon } from "Features/pages/purchaseOrders/icons/AIIcon"
import { defaultFilterInvoiceLink } from "Features/pages/purchaseOrders/queries/const"
import { usePurchaseOrdersForLinkingQuery } from "Features/pages/purchaseOrders/queries/purchaseOrder.queries"
import { ILineItem } from "Features/pages/purchaseOrders/types/LineItems.interfaces"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { CancelableComponent, ModalComponentPropsBase } from "Shared/context/modal.context"
import { PurchaseOrderReference } from "Shared/models/purchaseOrderReference"
import { isLineItemSplit } from "Shared/utils/expense.utils"
import { PurchaseOrderSelectionModal } from "../PurchaseOrderSelection.modal"
import { LineItemCard } from "./LineItemCard"
import { decorateItemsWithIndex, useLinkPurchaseOrder, type LinkedPair } from "./useLinkPurchaseOrder.hook"

type DecoratedLineItem = ILineItem & { index: number }

interface PurchaseOrderLinkModalProps extends ModalComponentPropsBase, Required<CancelableComponent> {
    expense: Expense
    selectedPurchaseOrders: Array<PurchaseOrderReference>
}

export const PurchaseOrderLinkModal = ({ expense, isShowing, onCancel, selectedPurchaseOrders }: PurchaseOrderLinkModalProps) => {
    const { t } = useTranslation()
    const [activePOId, setActivePOId] = useState<string>(selectedPurchaseOrders[0]?._id || "")
    const [additionalPurchaseOrders, setAdditionalPurchaseOrders] = useState<Array<PurchaseOrderReference>>([])
    const [isAdditionalModalOpen, setIsAdditionalModalOpen] = useState(false)
    const purchaseOrderReferences = [...selectedPurchaseOrders, ...additionalPurchaseOrders]
    const { data, isLoading: isPurchaseOrdersLoading } = usePurchaseOrdersForLinkingQuery({ filter: { _id: { in: purchaseOrderReferences.map((po) => po._id) } } })
    const purchaseOrders = data?.docs || []
    const {
        expenseSplits,
        handleInvoiceItemClick,
        handlePurchaseOrderItemClick,
        removeLink,
        clearAllLinks,
        resetAutoMatchState,
        isInvoiceItemLinked,
        isPurchaseOrderItemLinked,
        isItemActive,
        isPurchaseOrderItemDisabled,
        saveLinkChanges,
        hasChanges,
        linkedPairs,
        isPoLinkingDisabled,
        isClearAllDisabled,
        isLinkMutationLoading,
        autoMatchAll,
        isAutoMatchLoading,
        isAutoMatchDisabled,
        isInitialAutoMatch,
        hasNoAutoMatchResults,
        hasUnlinkedItems,
        hasPOsWithUnlinkedItems,
    } = useLinkPurchaseOrder(expense, purchaseOrders)
    const decoratedSelectedPODatas = purchaseOrders?.map((po) => ({ ...po, lineItems: decorateItemsWithIndex(po.lineItems) })) || []
    const decoratedSelectedPOData = decoratedSelectedPODatas.find((po) => po._id === activePOId)

    if (isPurchaseOrdersLoading) {
        return <Loading />
    }

    // Show loading screen during initial auto-match
    if (isInitialAutoMatch && isAutoMatchLoading) {
        return (
            <Modal title="" open={isShowing} onCancel={onCancel} maskClosable={false} closable={false} footer={null} width={700}>
                <div className="flex flex-col items-center justify-center gap-34 py-50 px-50">
                    <Spin size="large" />
                    <div className="flex items-center gap-8">
                        <AIIcon style={{ width: 32, height: 32 }} />
                        <div className="text-xl font-medium text-gray-900">{t("label:po_link.auto_matching_in_progress")}</div>
                    </div>
                </div>
            </Modal>
        )
    }

    // Debug early return conditions
    if (!expense || !isLineItemSplit(expense) || !purchaseOrders?.length || !decoratedSelectedPOData) {
        onCancel()
        Sentry.captureException(
            `[Purchase Order Link Modal] Error: In expense: ${expense.id} trying to link purchase orders: ${purchaseOrders.map((po) => po._id).join(", ")} but found no purchase orders`,
        )
        return null
    }
    // Separate linked and unlinked items while preserving original order
    // For invoice items: only show linked items if they're linked to the currently selected PO
    const linkedExpenseSplitsForCurrentPO = expenseSplits.filter((item) => {
        const isLinked = isInvoiceItemLinked(item?._id)
        if (!isLinked) {
            return false
        }
        // Check if this item is linked to the currently selected PO
        const linkedPair = linkedPairs.find((pair: LinkedPair) => pair.invoiceItemId === item._id!)
        return linkedPair?.poId === activePOId
    })

    // Only show items that are NOT linked to any PO
    const unlinkedExpenseSplits = expenseSplits.filter((item) => !isInvoiceItemLinked(item._id))

    // Order PO items to match the linked invoice items, so connection lines align properly
    const linkedPOItems = linkedExpenseSplitsForCurrentPO
        .map((invoiceItem) => {
            const linkedPair = linkedPairs.find((pair: LinkedPair) => pair.invoiceItemId === invoiceItem._id! && pair.poId === activePOId)
            return decoratedSelectedPOData.lineItems.find((poItem: DecoratedLineItem) => poItem._id === linkedPair?.poItemId)
        })
        .filter(Boolean) as Array<DecoratedLineItem>

    const unlinkedPOItems = decoratedSelectedPOData.lineItems.filter((item: DecoratedLineItem) => !isPurchaseOrderItemLinked(activePOId, item?._id))

    const onSubmit = async () => {
        await saveLinkChanges()
        onCancel()
    }

    const handleAdditionalPurchaseOrderSelection = (newPurchaseOrders: Array<PurchaseOrderReference>) => {
        const newPurchaseOrdersRefs = newPurchaseOrders.map((po) => ({
            _id: po._id,
            purchaseOrderNumber: po.purchaseOrderNumber,
            alreadyLinked: false,
        }))

        const isActivePoIdInSelected = selectedPurchaseOrders.find((po) => po._id === activePOId)
        const isActivePoIdInNewAdditional = newPurchaseOrdersRefs.find((po) => po._id === activePOId)

        if (!isActivePoIdInSelected && !isActivePoIdInNewAdditional && selectedPurchaseOrders?.[0]?._id) {
            // When the currently active PO is removed, go back to the first one to prevent the modal from closing.
            setActivePOId(selectedPurchaseOrders[0]._id)
        }

        setAdditionalPurchaseOrders(newPurchaseOrdersRefs)
        resetAutoMatchState()
    }

    const invoiceItemFields = (item: LineItem) => [
        { label: t("label:po_link.quantity"), value: item.quantity, key: "quantity" },
        { label: t("label:po_link.unitPrice"), value: formatCurrencyNumber({ value: item.unitPrice, currency: expense.currency }), key: "unitPrice" },
        { label: t("label:po_link.totalGross"), value: formatCurrencyNumber({ value: item.grossPrice, currency: expense.currency }), key: "grossPrice" },
    ]

    const poItemFields = (item: ILineItem) => [
        { label: t("label:po_link.quantity"), value: item.quantity ?? "", key: "quantity" },
        { label: t("label:po_link.quantityReceived"), value: calculateTotalQuantityReceived(item) ?? "", key: "quantityReceived" },
        {
            label: t("label:po_link.totalGross"),
            value: formatCurrencyNumber({ value: item.totalGrossPrice, currency: decoratedSelectedPOData.currency }),
            key: "totalGrossAmount",
        },
        {
            label: t("label:po_link.amountInvoiced"),
            value: formatCurrencyNumber({ value: calculateTotalAmountInvoiced(item), currency: decoratedSelectedPOData.currency }),
            key: "amountInvoiced",
        },
    ]

    return (
        <>
            <Modal
                title=""
                open={isShowing}
                onCancel={onCancel}
                maskClosable={true}
                className="purchase-order-link-modal ant-modal--full"
                footer={[
                    <Button key="cancel" onClick={onCancel}>
                        {t("action:cancel")}
                    </Button>,
                    <Button key="submit" type="primary" onClick={onSubmit} disabled={!hasChanges || isLinkMutationLoading} loading={isLinkMutationLoading}>
                        {t("action:po_link.submit_links")}
                    </Button>,
                ]}
            >
                <div className="h-full relative">
                    {/* Header row with both titles */}
                    <div
                        className="mb-10"
                        style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 100px 1fr",
                            gap: "0px",
                            height: "calc(100% - 100px)",
                        }}
                    >
                        <div style={{ maxWidth: "min(calc((98vw - 100px) / 2), 700px)" }}>
                            <h3 className="text-lg font-medium">{t("label:po_link.invoice_line_items")}</h3>
                            <div className="flex justify-between">
                                <Tooltip
                                    title={
                                        isAutoMatchDisabled ? (
                                            !hasUnlinkedItems ? (
                                                <div>{t("label:po_link.all_items_linked")}</div>
                                            ) : !hasPOsWithUnlinkedItems ? (
                                                <div>{t("label:po_link.all_po_items_linked")}</div>
                                            ) : hasNoAutoMatchResults ? (
                                                <div>{t("label:po_link.no_auto_match_results")}</div>
                                            ) : (
                                                <>
                                                    <div className="font-bold text-white"> {t("label:po_link.auto_link_tooltip_title")} </div>
                                                    <div> {t("label:po_link.auto_link_tooltip")}</div>
                                                </>
                                            )
                                        ) : undefined
                                    }
                                >
                                    <Button size="small" icon={<LinkIcon color={COLOR.text} />} onClick={autoMatchAll} disabled={isAutoMatchDisabled} loading={isAutoMatchLoading}>
                                        {t("action:po_link.auto_link_all")}
                                    </Button>
                                </Tooltip>
                                <Button size="small" icon={<UnlinkIcon color={COLOR.text} />} onClick={clearAllLinks} disabled={isClearAllDisabled}>
                                    {t("action:po_link.clear_all")}
                                </Button>
                            </div>
                        </div>
                        <div></div>
                        <div style={{ maxWidth: "min(calc((98vw - 100px) / 2), 700px)" }}>
                            <h3 className="text-lg font-medium">{t("label:po_link.purchase_order_line_items")}</h3>
                            <div className="flex justify-between w-full">
                                <div className="flex items-center gap-2 mt-2 overflow-x-auto">
                                    {purchaseOrderReferences.map((po) => (
                                        <Tag
                                            key={po._id}
                                            onClick={() => {
                                                setActivePOId(po._id)
                                            }}
                                            style={{
                                                borderColor: activePOId === po._id ? COLOR.finway.green : "transparent",
                                                backgroundColor: activePOId === po._id ? "white" : "transparent",
                                            }}
                                            className={`mb-4 flex items-center align-middle cursor-pointer`}
                                        >
                                            <PurchaseOrderTagIcon className="mr-4" />
                                            {po.purchaseOrderNumber}
                                        </Tag>
                                    ))}
                                </div>
                                <Button icon={<PlusOutlined />} onClick={() => setIsAdditionalModalOpen(true)} />
                            </div>
                        </div>
                    </div>

                    {/* Content row with 3 columns */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 100px 1fr", gap: "0px", height: "calc(100% - 100px)" }}>
                        <div>
                            <div className="space-y-4">
                                {/* Linked items */}
                                {linkedExpenseSplitsForCurrentPO.map((item: LineItem & { index: number }, indx: number) => {
                                    const linkedPair = linkedPairs.find((pair: LinkedPair) => pair.invoiceItemId === item._id!)
                                    return (
                                        <LineItemCard
                                            key={indx}
                                            index={item.index}
                                            materialCode={item.materialCode || ""}
                                            name={item.name || ""}
                                            fields={invoiceItemFields(item)}
                                            isActive={isItemActive(item?._id)}
                                            isLinked={true}
                                            isAutoMatched={linkedPair?.isAutoMatched}
                                            onClick={() => handleInvoiceItemClick(item._id!)}
                                        />
                                    )
                                })}

                                {/* Unlinked items */}
                                {unlinkedExpenseSplits.map((item: LineItem & { index: number }, indx: number) => {
                                    const active = isItemActive(item?._id)

                                    return (
                                        <LineItemCard
                                            key={indx}
                                            index={item.index}
                                            materialCode={item.materialCode || ""}
                                            name={item.name || ""}
                                            fields={invoiceItemFields(item)}
                                            isActive={active}
                                            isLinked={false}
                                            onClick={() => handleInvoiceItemClick(item._id!)}
                                        />
                                    )
                                })}
                            </div>
                        </div>

                        {/* Middle column for connection lines */}
                        <div className="grid w-100" style={{ gridAutoRows: "1fr" }}>
                            {linkedPairs.map((pair, indx) => {
                                const invoiceIndex = linkedExpenseSplitsForCurrentPO.findIndex((item: LineItem) => item._id === pair.invoiceItemId)
                                const purchaseIndex = linkedPOItems.findIndex((item: DecoratedLineItem) => item._id === pair.poItemId)

                                if (invoiceIndex === -1 || purchaseIndex === -1) {
                                    return null
                                }

                                const linkColor = pair.isAutoMatched ? COLOR.finway["purple-accent"] : COLOR.finway.green

                                return (
                                    <div key={indx} className="flex flex-col items-center">
                                        <svg className="h-1 w-full relative" style={{ top: "50%" }}>
                                            <line x1="0" y1="0" x2="100%" y2="0" stroke={linkColor} strokeWidth="2" strokeDasharray="5,5" />
                                        </svg>
                                        <div className="relative" style={{ top: "calc(50% - 16px)" }}>
                                            <Tooltip title={t("action:po_link.unlink")}>
                                                <UnlinkIcon
                                                    bgColor="#231C5B"
                                                    className={isPoLinkingDisabled(pair.poId) ? "cursor-not-allowed" : "cursor-pointer"}
                                                    onClick={() => removeLink(pair.invoiceItemId, pair.poItemId, pair.poId)}
                                                />
                                            </Tooltip>
                                        </div>
                                    </div>
                                )
                            })}

                            {/* fill with placeholders for unlinked items */}
                            {(unlinkedExpenseSplits.length > unlinkedPOItems.length ? unlinkedExpenseSplits : unlinkedPOItems).map((_, indx: number) => (
                                <div key={`empty-${indx}`}></div>
                            ))}
                        </div>

                        {/* Right column for purchase order items */}
                        <div>
                            {decoratedSelectedPOData && (
                                <div className="space-y-4">
                                    {linkedPOItems.map((item: DecoratedLineItem, indx: number) => {
                                        const linkedPair = linkedPairs.find((pair: LinkedPair) => pair.poItemId === item._id! && pair.poId === activePOId)
                                        return (
                                            <LineItemCard
                                                key={indx}
                                                index={item.index}
                                                materialCode={item.materialCode ?? ""}
                                                name={item.name}
                                                fields={poItemFields(item)}
                                                isLinked={true}
                                                isAutoMatched={linkedPair?.isAutoMatched}
                                                onClick={() => handlePurchaseOrderItemClick(item._id!, activePOId)}
                                            />
                                        )
                                    })}

                                    {unlinkedPOItems.map((item: DecoratedLineItem, indx: number) => {
                                        const active = isItemActive(item?._id)
                                        const disabled = isPurchaseOrderItemDisabled(activePOId, item._id!)

                                        return (
                                            <LineItemCard
                                                key={indx}
                                                index={item.index}
                                                materialCode={item.materialCode ?? ""}
                                                name={item.name}
                                                fields={poItemFields(item)}
                                                isActive={active}
                                                isLinked={false}
                                                isDisabled={disabled}
                                                onClick={() => !disabled && handlePurchaseOrderItemClick(item._id!, activePOId)}
                                            />
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Modal>
            <PurchaseOrderSelectionModal
                isShowing={isAdditionalModalOpen}
                onCancel={() => {
                    setIsAdditionalModalOpen(false)
                }}
                onSelection={handleAdditionalPurchaseOrderSelection}
                excludePurchaseOrderIds={selectedPurchaseOrders.map((po) => po._id)}
                defaultFilter={defaultFilterInvoiceLink}
            />
        </>
    )
}
